import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { usePermissions } from '@/hooks/usePermission'
import { Button } from '@/components/ui/Button'

export interface PunchItem { id: string; title: string; done: boolean }

interface Props {
  items: PunchItem[]
  canEdit: boolean
  busy?: boolean
  onAdd: (title: string) => Promise<void>
  onToggle: (item: PunchItem) => Promise<void>
}

export function PunchList({ items, canEdit, busy, onAdd, onToggle }: Props) {
  const id = useId()
  const [title, setTitle] = useState('')
  const [error, setError] = useState('')
  const done = items.filter(item => item.done).length
  return (
    <section aria-labelledby={id} className="space-y-3 min-w-0">
      <h3 id={id} className="font-semibold">Punch list</h3>
      <p className="text-sm" style={{ color: 'var(--midnight-text3)' }}>
        {items.length ? `${done} of ${items.length} done · ${items.length - done} open` : 'No punch-list items yet. Nothing has been checked off.'}
      </p>
      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map(item => (
            <li key={item.id} className="ds-tile-inset p-3">
              <label className="flex items-start gap-3 min-h-11 cursor-pointer">
                <input type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={item.done} disabled={!canEdit || busy}
                  onChange={async () => {
                    setError('')
                    try { await onToggle(item) } catch { setError('Could not update this item. Try again.') }
                  }} />
                <span className="min-w-0 flex-1">
                  <span className={item.done ? 'line-through break-words' : 'break-words'} style={{ overflowWrap: 'anywhere' }}>{item.title}</span>
                  <span className="block text-xs" style={{ color: 'var(--midnight-text3)' }}>{item.done ? 'Done' : 'Open'}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 && done === items.length && <p className="text-sm">All punch-list items are done. Job status is unchanged.</p>}
      {canEdit && (
        <form className="space-y-2" onSubmit={async event => {
          event.preventDefault()
          if (!title.trim() || busy) return
          setError('')
          try { await onAdd(title.trim()); setTitle('') } catch { setError('Could not add this item. Your text is still here. Try again.') }
        }}>
          <label htmlFor={`${id}-new`} className="block text-sm">New punch-list item</label>
          <input id={`${id}-new`} className="input-field w-full min-w-0" maxLength={300} value={title} disabled={busy}
            onChange={event => setTitle(event.target.value)} placeholder="e.g. Touch up the door trim" />
          <Button type="submit" variant="secondary" disabled={!title.trim() || busy}>Add item</Button>
        </form>
      )}
      {error && <p role="alert" className="text-sm">{error}</p>}
    </section>
  )
}

export default function JobPunchList({ jobId }: { jobId: string }) {
  const { can } = usePermissions()
  const client = useQueryClient()
  const queryKey = ['job-punch-list', jobId]
  const path = `/api/jobs/${encodeURIComponent(jobId)}/punch-list`
  const query = useQuery({ queryKey, queryFn: async () => {
    const response = await apiClient.get<{ data: PunchItem[] }>(path)
    return response.data.data
  } })
  const mutation = useMutation({
    mutationFn: async (action: { title: string } | { item: PunchItem }) => {
      if ('title' in action) await apiClient.post(path, action)
      else await apiClient.patch(`${path}/${encodeURIComponent(action.item.id)}`, { done: !action.item.done })
    },
    onSuccess: () => client.invalidateQueries({ queryKey }),
  })
  if (query.isPending) return <p role="status">Loading punch list…</p>
  if (query.isError) return <div role="alert" className="space-y-2"><p>Punch list unavailable. Items could not be loaded.</p><Button variant="secondary" onClick={() => void query.refetch()}>Retry punch list</Button></div>
  return <PunchList key={jobId} items={query.data} canEdit={can('jobs.edit') || can('jobs.complete')} busy={mutation.isPending}
    onAdd={title => mutation.mutateAsync({ title })} onToggle={item => mutation.mutateAsync({ item })} />
}
